import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Tabs, Tab, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Checkbox, Button, Chip,
    TextField, MenuItem, Select, FormControl, InputLabel, Avatar,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    Grid, Card, CardContent, LinearProgress, Alert, Snackbar,
    Tooltip, Divider, useTheme, useMediaQuery, Stack, CircularProgress, Container
} from '@mui/material';
import {
    Person, AttachMoney, CheckCircle, Pending, CalendarMonth,
    FilterList, Payment, ArrowBack, Info, Refresh, Today,
    Event, ArrowDropDown, ArrowDropUp, AccountBalance, 
    ReceiptLong, History, Groups, AccountBalanceWallet
} from '@mui/icons-material';
import styled from 'styled-components';
import {
    getEmployeesWithSalaryStatus,
    bulkSalaryPayment,
    createOrUpdateSalary,
    recordSalaryPayment
} from '../../../redux/salaryRelated/salaryHandle';
import { useToast } from '../../../context/ToastContext';
import { underControl, clearEmployees } from '../../../redux/salaryRelated/salarySlice';
import { getNepaliMonthOptions, getNepaliYearOptions, getCurrentBSDate, formatDatePart } from '../../../utils/customDate';

// Month options
// Date options
const months = getNepaliMonthOptions();
const years = getNepaliYearOptions();
const currentBS = getCurrentBSDate().split('-');
const initialMonth = currentBS[1];
const initialYear = parseInt(currentBS[0]);

// Styled Components for Premium UI
const GlassCard = styled(Card)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05) !important;
    transition: all 0.3s ease;
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1) !important;
    }
`;

const GradientHeader = styled(Box)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-radius: 0 0 40px 40px;
    padding: 40px 0;
    margin-bottom: -40px;
    color: white;
`;

const StatIcon = styled(Box)`
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.bg || 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.color || 'white'};
`;

const isCurrentPeriod = (month, year) => {
    const [currYear, currMonth] = getCurrentBSDate().split('-');
    return month === currMonth && parseInt(year) === parseInt(currYear);
};

const AddSalary = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { employeesWithSalary, loading, error, status } = useSelector((state) => state.salary);
    const { currentUser } = useSelector((state) => state.user);
    const adminID = currentUser._id;

    const [tabValue, setTabValue] = useState(0); // 0 for Teachers, 1 for Staff
    const [month, setMonth] = useState(initialMonth);
    const [year, setYear] = useState(initialYear);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [processing, setProcessing] = useState(false);

    // Dialog states
    const [openSalaryDialog, setOpenSalaryDialog] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [openPaymentDialog, setOpenPaymentDialog] = useState(false);

    // Salary Form state
    const [salaryFormData, setSalaryFormData] = useState({
        baseSalary: '',
        houseRent: '0',
        medical: '0',
        transport: '0',
        other: '0',
        providentFund: '0',
        tax: '0',
        insurance: '0',
        otherDeduction: '0'
    });

    useEffect(() => {
        fetchEmployees();
    }, [tabValue, month, year]);

    useEffect(() => {
        if (status === "added" || status === "success") {
            showToast("Salary Configuration Saved Successfully", "success");
            setOpenSalaryDialog(false);
            fetchEmployees();
            dispatch(underControl());
        } else if (status === "paid") {
            showToast("Salary Disbursed Successfully", "success");
            setOpenPaymentDialog(false);
            fetchEmployees();
            dispatch(underControl());
        }
    }, [status]);

    const fetchEmployees = () => {
        const category = tabValue === 0 ? 'Teacher' : 'Staff';
        dispatch(getEmployeesWithSalaryStatus(adminID, category, month, year));
        setSelectedEmployees([]);
        setSelectAll(false);
    };

    const handleTabChange = (event, newValue) => {
        dispatch(clearEmployees());
        setTabValue(newValue);
    };

    const handleSelectAll = (event) => {
        setSelectAll(event.target.checked);
        if (event.target.checked) {
            setSelectedEmployees(employeesWithSalary.map(emp => emp._id));
        } else {
            setSelectedEmployees([]);
        }
    };

    const handleSelectEmployee = (id) => {
        const currentIndex = selectedEmployees.indexOf(id);
        const newSelected = [...selectedEmployees];

        if (currentIndex === -1) {
            newSelected.push(id);
        } else {
            newSelected.splice(currentIndex, 1);
        }

        setSelectedEmployees(newSelected);
        setSelectAll(newSelected.length === employeesWithSalary.length);
    };

    const openSalarySetup = (employee) => {
        setSelectedEmployee(employee);
        if (employee.hasSalaryRecord) {
            setSalaryFormData({
                baseSalary: employee.baseSalary || '',
                houseRent: employee.houseRent || '0',
                medical: employee.medical || '0',
                transport: employee.transport || '0',
                other: employee.other || '0',
                providentFund: employee.providentFund || '0',
                tax: employee.tax || '0',
                insurance: employee.insurance || '0',
                otherDeduction: employee.otherDeduction || '0'
            });
        } else {
            setSalaryFormData({
                baseSalary: '',
                houseRent: '0',
                medical: '0',
                transport: '0',
                other: '0',
                providentFund: '0',
                tax: '0',
                insurance: '0',
                otherDeduction: '0'
            });
        }
        setOpenSalaryDialog(true);
    };

    const handleSaveSalary = () => {
        if (!salaryFormData.baseSalary) {
            showToast("Base salary is required", "error");
            return;
        }

        const data = {
            school: adminID,
            employeeId: selectedEmployee._id,
            employeeType: tabValue === 0 ? 'teacher' : 'staff',
            position: selectedEmployee.position || (tabValue === 0 ? 'Teacher' : 'Staff'),
            baseSalary: parseFloat(salaryFormData.baseSalary),
            allowances: {
                houseRent: parseFloat(salaryFormData.houseRent) || 0,
                medical: parseFloat(salaryFormData.medical) || 0,
                transport: parseFloat(salaryFormData.transport) || 0,
                other: parseFloat(salaryFormData.other) || 0
            },
            deductions: {
                providentFund: parseFloat(salaryFormData.providentFund) || 0,
                tax: parseFloat(salaryFormData.tax) || 0,
                insurance: parseFloat(salaryFormData.insurance) || 0,
                other: parseFloat(salaryFormData.otherDeduction) || 0
            },
            effectiveDate: getCurrentBSDate()
        };

        setProcessing(true);
        dispatch(createOrUpdateSalary(data))
            .finally(() => setProcessing(false));
    };

    const handleSinglePayment = (employee) => {
        setSelectedEmployee(employee);
        setOpenPaymentDialog(true);
    };

    const handleBulkPayment = () => {
        setSelectedEmployee(null);
        setOpenPaymentDialog(true);
    };

    const processPayment = () => {
        setProcessing(true);
        const data = {
            employeeIds: selectedEmployees,
            employeeModel: tabValue === 0 ? 'Teacher' : 'Staff',
            month,
            year,
            paymentMethod,
            school: adminID
        };

        dispatch(bulkSalaryPayment(data))
            .finally(() => {
                setProcessing(false);
                setOpenPaymentDialog(false);
            });
    };

    const processSinglePayment = () => {
        setProcessing(true);
        const data = {
            employeeId: selectedEmployee._id,
            employeeType: tabValue === 0 ? 'teacher' : 'staff',
            month,
            year,
            paymentMethod,
            school: adminID,
            amount: selectedEmployee.netSalary
        };

        dispatch(recordSalaryPayment(selectedEmployee.salaryId || 'direct', data))
            .finally(() => {
                setProcessing(true); // Keep loading while we refresh
                fetchEmployees();
                setProcessing(false);
                setOpenPaymentDialog(false);
            });
    };

    const calculateNetSalary = () => {
        const base = parseFloat(salaryFormData.baseSalary) || 0;
        const allowances = (parseFloat(salaryFormData.houseRent) || 0) +
            (parseFloat(salaryFormData.medical) || 0) +
            (parseFloat(salaryFormData.transport) || 0) +
            (parseFloat(salaryFormData.other) || 0);
        const deductions = (parseFloat(salaryFormData.providentFund) || 0) +
            (parseFloat(salaryFormData.tax) || 0) +
            (parseFloat(salaryFormData.insurance) || 0) +
            (parseFloat(salaryFormData.otherDeduction) || 0);
        return base + allowances - deductions;
    };

    const getSelectedTotal = () => {
        return employeesWithSalary
            .filter(emp => selectedEmployees.includes(emp._id))
            .reduce((sum, emp) => sum + (emp.netSalary || emp.baseSalary || 0), 0);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-NP', {
            style: 'currency',
            currency: 'NPR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getStatusChip = (employee) => {
        if (!employee.hasSalaryRecord) {
            return <Chip label="Unconfigured" color="error" size="small" variant="outlined" sx={{ borderRadius: '6px', fontWeight: 700 }} />;
        }
        if (!employee.isPaidForSelectedMonth) {
            return <Chip label="Pending" color="warning" size="small" sx={{ borderRadius: '6px', fontWeight: 700 }} />;
        }
        return <Chip icon={<CheckCircle />} label="Distributed" color="success" size="small" sx={{ borderRadius: '6px', fontWeight: 700 }} />;
    };

    const stats = {
        total: employeesWithSalary.length,
        paid: employeesWithSalary.filter(e => e.isPaidForSelectedMonth).length,
        pending: employeesWithSalary.filter(e => !e.isPaidForSelectedMonth && e.hasSalaryRecord).length,
        noSalary: employeesWithSalary.filter(e => !e.hasSalaryRecord).length,
        totalAmount: employeesWithSalary.reduce((sum, e) => sum + (e.netSalary || 0), 0)
    };

    if (loading && !employeesWithSalary.length) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
                <CircularProgress size={60} thickness={4} sx={{ color: '#7f56da' }} />
                <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Orchestrating Salary Data...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <GradientHeader>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                        <IconButton 
                            onClick={() => navigate('/Admin/salary')} 
                            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                                Salary Distribution
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                Institutional Payroll Management System
                            </Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            variant="contained"
                            startIcon={<History />}
                            onClick={() => navigate('/Admin/salary')}
                            sx={{ bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, borderRadius: '12px', textTransform: 'none' }}
                        >
                            View History
                        </Button>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Total Employees" value={stats.total} icon={<Groups />} color="#6366f1" bg="rgba(99, 102, 241, 0.2)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Paid Distribution" value={stats.paid} icon={<CheckCircle />} color="#10b981" bg="rgba(16, 185, 129, 0.2)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Pending Payouts" value={stats.pending} icon={<Pending />} color="#f59e0b" bg="rgba(245, 158, 11, 0.2)" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard title="Total Volume" value={formatCurrency(stats.totalAmount)} icon={<AccountBalanceWallet />} color="#8b5cf6" bg="rgba(139, 92, 246, 0.2)" />
                        </Grid>
                    </Grid>
                </Container>
            </GradientHeader>

            <Container maxWidth="xl" sx={{ mt: 8, pb: 10 }}>
                {/* Control Panel */}
                <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{ 
                            bgcolor: 'white', 
                            borderRadius: '16px', 
                            p: 0.5,
                            '& .MuiTabs-indicator': { display: 'none' },
                            '& .MuiTab-root': {
                                borderRadius: '12px',
                                minHeight: '44px',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&.Mui-selected': { bgcolor: '#1a1a2e', color: 'white' }
                            }
                        }}
                    >
                        <Tab label="Teachers" />
                        <Tab label="Staff Members" />
                    </Tabs>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', bgcolor: 'white', p: 1, borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        <CalendarMonth sx={{ color: 'text.secondary', ml: 1 }} />
                        <Select
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                            size="small"
                            sx={{ border: 'none', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                        >
                            {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </Select>
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <Select
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            size="small"
                            sx={{ border: 'none', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 700 }}
                        >
                            {years.map(y => <MenuItem key={y.value} value={y.value}>{y.label}</MenuItem>)}
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 160, bgcolor: 'white', borderRadius: '12px' }}>
                            <InputLabel>Payment Mode</InputLabel>
                            <Select value={paymentMethod} label="Payment Mode" onChange={(e) => setPaymentMethod(e.target.value)} sx={{ borderRadius: '12px' }}>
                                <MenuItem value="cash">Cash Settlement</MenuItem>
                                <MenuItem value="bank">Bank Transfer</MenuItem>
                                <MenuItem value="digital">Digital Wallet</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={selectedEmployees.length === 0 || processing}
                            onClick={handleBulkPayment}
                            sx={{ borderRadius: '12px', px: 4, py: 1.2, fontWeight: 700, textTransform: 'none', boxShadow: '0 4px 14px 0 rgba(0, 118, 255, 0.39)' }}
                        >
                            Distribute Salaries ({selectedEmployees.length})
                        </Button>
                    </Box>
                </Box>

                {/* Table Section */}
                <GlassCard>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={selectAll} indeterminate={selectedEmployees.length > 0 && selectedEmployees.length < employeesWithSalary.length} onChange={handleSelectAll} />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Employee Details</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Monthly Breakdown</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Net Payout</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Operations</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {employeesWithSalary.map((emp) => (
                                    <TableRow key={emp._id} hover sx={{ transition: '0.2s' }}>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selectedEmployees.includes(emp._id)} onChange={() => handleSelectEmployee(emp._id)} />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar src={emp.photo ? `${process.env.REACT_APP_BASE_URL}/${emp.photo}` : ''} sx={{ width: 44, height: 44, bgcolor: '#7f56da' }}>
                                                    {emp.name?.charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{emp.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{emp.email}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Base Salary">
                                                    <Chip label={formatCurrency(emp.baseSalary)} size="small" variant="outlined" />
                                                </Tooltip>
                                                <Tooltip title="Net Allowances">
                                                    <Chip label={`+${formatCurrency(emp.totalAllowances)}`} size="small" sx={{ bgcolor: '#ecfdf5', color: '#10b981', border: 'none' }} />
                                                </Tooltip>
                                                <Tooltip title="Net Deductions">
                                                    <Chip label={`-${formatCurrency(emp.totalDeductions)}`} size="small" sx={{ bgcolor: '#fef2f2', color: '#ef4444', border: 'none' }} />
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#1a1a2e' }}>
                                                {formatCurrency(emp.netSalary)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {getStatusChip(emp)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton onClick={() => openSalarySetup(emp)} sx={{ bgcolor: '#f8fafc', color: '#64748b' }} size="small">
                                                    <ReceiptLong fontSize="small" />
                                                </IconButton>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color={emp.isPaidForSelectedMonth ? "info" : "success"}
                                                    disabled={emp.isPaidForSelectedMonth || !emp.hasSalaryRecord}
                                                    onClick={() => handleSinglePayment(emp)}
                                                    sx={{ borderRadius: '8px', textTransform: 'none', px: 3 }}
                                                >
                                                    {emp.isPaidForSelectedMonth ? "Distributed" : "Pay Now"}
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </GlassCard>
            </Container>

            {/* Processing Indicator */}
            {processing && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999 }} />}

            {/* Salary Setup Dialog */}
            <Dialog open={openSalaryDialog} onClose={() => setOpenSalaryDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 2 } }}>
                <DialogTitle>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Salary Configuration</Typography>
                    <Typography variant="body2" color="text.secondary">Defining parameters for {selectedEmployee?.name}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={4} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AttachMoney color="primary" /> Basic Compensation
                            </Typography>
                            <TextField fullWidth label="Base Salary" type="number" value={salaryFormData.baseSalary} onChange={(e) => setSalaryFormData({ ...salaryFormData, baseSalary: e.target.value })} InputProps={{ startAdornment: 'Rs. ' }} sx={{ mb: 3 }} />
                            
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalance color="success" /> Allowances
                            </Typography>
                            <Grid container spacing={2}>
                                {['houseRent', 'medical', 'transport', 'other'].map(field => (
                                    <Grid item xs={6} key={field}>
                                        <TextField fullWidth label={field.charAt(0).toUpperCase() + field.slice(1)} type="number" value={salaryFormData[field]} onChange={(e) => setSalaryFormData({ ...salaryFormData, [field]: e.target.value })} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccountBalanceWallet color="error" /> Deductions
                            </Typography>
                            <Grid container spacing={2}>
                                {['providentFund', 'tax', 'insurance', 'otherDeduction'].map(field => (
                                    <Grid item xs={6} key={field}>
                                        <TextField fullWidth label={field.replace('Deduction', '')} type="number" value={salaryFormData[field]} onChange={(e) => setSalaryFormData({ ...salaryFormData, [field]: e.target.value })} />
                                    </Grid>
                                ))}
                            </Grid>

                            <Box sx={{ mt: 4, p: 3, bgcolor: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Payout Summary</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography color="text.secondary">Gross Total</Typography>
                                    <Typography sx={{ fontWeight: 600 }}>{formatCurrency((parseFloat(salaryFormData.baseSalary) || 0) + (parseFloat(salaryFormData.houseRent) || 0) + (parseFloat(salaryFormData.medical) || 0) + (parseFloat(salaryFormData.transport) || 0) + (parseFloat(salaryFormData.other) || 0))}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#ef4444' }}>
                                    <Typography>Total Deductions</Typography>
                                    <Typography sx={{ fontWeight: 600 }}>-{formatCurrency((parseFloat(salaryFormData.providentFund) || 0) + (parseFloat(salaryFormData.tax) || 0) + (parseFloat(salaryFormData.insurance) || 0) + (parseFloat(salaryFormData.otherDeduction) || 0))}</Typography>
                                </Box>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', color: '#1a1a2e' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>Net Payable</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{formatCurrency(calculateNetSalary())}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenSalaryDialog(false)} sx={{ color: 'text.secondary', fontWeight: 600 }}>Discard</Button>
                    <Button variant="contained" onClick={handleSaveSalary} disabled={processing} sx={{ bgcolor: '#1a1a2e', borderRadius: '12px', px: 4 }}>Save Configuration</Button>
                </DialogActions>
            </Dialog>

            {/* Payment Confirmation Dialog */}
            <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
                <DialogTitle>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Authorize Disbursement</Typography>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
                        You are about to authorize salary distribution for <strong>{selectedEmployee ? selectedEmployee.name : `${selectedEmployees.length} employees`}</strong> for the period of <strong>{months.find(m => m.value === month)?.label || month} {year}</strong>.
                    </Alert>
                    <Box sx={{ bgcolor: '#f1f5f9', p: 3, borderRadius: '16px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography color="text.secondary">Total Disbursement</Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem' }}>{formatCurrency(selectedEmployee ? (selectedEmployee.netSalary || selectedEmployee.baseSalary) : getSelectedTotal())}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography color="text.secondary">Payment Channel</Typography>
                            <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{paymentMethod}</Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenPaymentDialog(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={selectedEmployee ? processSinglePayment : processPayment} disabled={processing} sx={{ borderRadius: '12px', px: 4, fontWeight: 700 }}>Confirm Disbursement</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Sub-components
const StatCard = ({ title, value, icon, color, bg }) => (
    <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', p: 2, borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <StatIcon bg={bg} color={color}>{icon}</StatIcon>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{title}</Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{value}</Typography>
    </Box>
);

export default AddSalary;
